/**
 * Version-aware adapter factory. The store uses this so the rest of
 * its boot path can be written against the version-neutral
 * `OcppAdapter` interface.
 *
 * Why a factory and not just `new OcppVxxAdapter(...)` at call sites:
 *   - The store wires `wrapCallClearingErrorOnSuccess` around the sink's
 *     `call(...)` method (see BUG-002 fix in `state/clearErrorOnSuccess`).
 *     Repeating that around two different `new` calls would invite
 *     drift between v16 and v201 boot paths.
 *   - 10c added per-tx state to the v201 adapter and a topology getter
 *     in 10b. The factory passes those through when the version is
 *     2.0.1 and ignores them otherwise.
 */
import type { Topology } from '@/domain/Topology';
import type { OcppVersion } from '@/domain/types';
import type { OcppAdapter } from '@/protocol/OcppAdapter';
import { OcppV16Adapter } from '@/protocol/v16/OcppV16Adapter';
import { OcppV201Adapter } from '@/protocol/v201/OcppV201Adapter';
import { OcppV21Adapter } from '@/protocol/v21/OcppV21Adapter';

export interface AdapterSink {
  call(
    action: string,
    payload: Record<string, unknown>,
    timeoutMs?: number,
  ): { messageId: string; response: Promise<Record<string, unknown>> };
}

export interface CreateAdapterOptions {
  readonly version: OcppVersion;
  readonly sink: AdapterSink;
  /** Honoured for 2.0.1 + 2.1 — provides topology for inbound `(evseId, …)` resolution. */
  readonly topology?: () => Topology | null;
}

export function createAdapter(opts: CreateAdapterOptions): OcppAdapter {
  if (opts.version === '2.0.1') {
    return new OcppV201Adapter({
      sink: opts.sink,
      ...(opts.topology ? { topology: opts.topology } : {}),
    });
  }
  if (opts.version === '2.1') {
    // 2.1 reuses the 2.0.1 adapter (backward-compatible superset) with the 2.1
    // schema registry + `ocpp2.1` subprotocol. Topology resolution is honoured
    // identically to 2.0.1.
    return new OcppV21Adapter({
      sink: opts.sink,
      ...(opts.topology ? { topology: opts.topology } : {}),
    });
  }
  return new OcppV16Adapter(opts.sink);
}
